import { classifyTransportError } from './errors';
import { SUPPRESS_GLOBAL_ERROR_META } from './mutationErrorPolicy';

/**
 * The toolkit for a call site that reports its OWN mutation failures.
 *
 * Three things go wrong when a component draws `{mutation.isError && <Alert/>}`
 * and nothing else is arranged, and all three are handled here rather than
 * re-derived in each screen:
 *
 *   1. The app-wide MutationCache net cannot see JSX, so it toasts the same
 *      failure a second time in weaker words. `REPORTS_OWN_ERRORS` is how a
 *      call site declares that it has the failure covered.
 *   2. React Query holds `error` until that same mutation runs again, so a page
 *      reading several mutations accumulates stale failures. `latestFailure`
 *      picks by clock instead of by the order they happen to be listed in.
 *   3. A timeout is not a failure — the write may have landed. `outcomeUnknown`
 *      is the flag that stops a message asserting "nothing moved".
 */

/**
 * A mutation's `meta` when its failure is already rendered where the user will
 * see it, so the app-wide net must stay off it.
 *
 * Built from the policy's own exported key rather than a hand-written string:
 * the net matches that key exactly, and a silent mismatch here would restore a
 * second, vaguer error message on seven money-touching actions with nothing
 * failing to say so. callSiteErrors.test.ts asserts the policy really does read
 * this object as out-of-net; inlineErrorCallSites.test.tsx asserts the seven
 * hooks that carry it stay silent through the real QueryClient.
 */
export const REPORTS_OWN_ERRORS: Record<string, unknown> = { [SUPPRESS_GLOBAL_ERROR_META]: true };

export interface FailedAttempt {
  error: unknown;
  /** React Query's `submittedAt` for the mutation this attempt reads. */
  at?: number;
}

/**
 * The attempt whose failure is newest, or null when none failed.
 *
 * Scanning in array order would let a cancel that failed an hour ago mask an
 * export that failed a second ago, because both errors are still live on their
 * mutations. Attempts with no clock sort oldest, which keeps a caller that has
 * not wired `submittedAt` on the old first-wins behaviour rather than crashing.
 */
export function latestFailure<T extends FailedAttempt>(attempts: T[]): T | null {
  let newest: T | null = null;
  for (const attempt of attempts) {
    if (!attempt.error) continue;
    if (!newest || (attempt.at ?? 0) > (newest.at ?? 0)) newest = attempt;
  }
  return newest;
}

/**
 * True when we cannot say whether the write landed.
 *
 * Only a timeout is genuinely ambiguous: the request was still open when our
 * clock ran out, so the server may well have committed it. A response — even a
 * 500 — and a request that never got a connection both leave the record
 * definitively unchanged, which is what lets those messages promise that no
 * money moved.
 */
export function outcomeUnknown(error: unknown): boolean {
  return classifyTransportError(error) === 'timeout';
}
